import React from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql, Link } from 'gatsby';

import { useSiteMetaContext } from '../context/SiteMetaContext';
// import { facebook, instagram, twitter } from '../images/icons';
import { facebook, instagram } from '../images/icons';
import socialLinks from '../constants/socialLinks';
import Container from '../layout/Container';
import LeftImageCard from '../components/LeftImageCard';
import CalendarButton from '../components/CalendarButton';
import Icon from '../components/Icon';
import PaidContent from '../components/PaidContent';

const Background = styled.div`
  background-color: ${({ theme }) => theme.colours.backgroundPaper};
  background: repeating-linear-gradient(
    ${({ theme }) => theme.colours.white},
    ${({ theme }) => theme.colours.white} 1%,
    ${({ theme }) => theme.colours.backgroundPaper} 1.1%,
    ${({ theme }) => theme.colours.backgroundPaper} 100%
  );
`;

const Wrapper = styled(Container)`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 0px;
`;

const Card = styled(LeftImageCard)`
  position: relative;
  background-color: ${({ theme }) => theme.colours.backgroundAlt};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  margin-bottom: ${({ theme }) => theme.sizes.lvl8};
`;
const StepCard = styled(Card)`
  color: ${({ theme }) => theme.colours.white};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const GiftCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.sizes.lvl0};
  color: white;
  a {
    color: white;
  }
`;

const CardIndexWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: -${({ theme }) => theme.sizes.lvl4};
  margin: auto 0;
  background-color: ${({ theme }) => theme.colours.backgroundPaper};
  width: ${({ theme }) => theme.sizes.lvl8};
  height: ${({ theme }) => theme.sizes.lvl8};
  border-radius: ${({ theme }) => theme.sizes.lvl8};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Index = styled.h3`
  && {
    font-family: ${({ theme }) => theme.font.families.title};
    font-weight: 400;
    color: ${({ theme }) => theme.colours.primary};
    font-size: ${({ theme }) => theme.sizes.lvl3};
    line-height: ${({ theme }) => theme.sizes.lvl3};
    padding: 0;
    margin: 0;
    display: flex;
  }
`;

const CardTitle = styled.h3`
  color: ${({ theme }) => theme.colours.white};
`;

const CardLink = styled(Link)`
  color: ${({ theme }) => theme.colours.white};
  text-align: center;
  overflow-wrap: break-word;
  font-family: ${({ theme }) => theme.font.families.title} !important;
  font-size: ${({ theme }) => theme.font.sizes.md};
  ${({ theme }) => theme.breakpoint('medium')`
    font-size: ${theme.font.sizes.xxl};
  `}
  padding: 0px;
  margin: 0px;
`;

const CardContent = styled.p`
  padding-top: ${({ theme }) => theme.sizes.lvl3};
  margin: 0px;
  color: ${({ theme }) => theme.colours.white};
  text-align: center;
  font-weight: 100;
  letter-spacing: 1px;
`;

const SocialWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.sizes.lvl1} 0px;
`;
const SocialCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.sizes.lvl5};
  height: ${({ theme }) => theme.sizes.lvl5};
  border-radius: ${({ theme }) => theme.sizes.lvl5};
  background-color: ${({ theme }) => theme.colours.backgroundPaper};
  cursor: pointer;

  ${({ theme }) => theme.breakpoint('tabletPortrait')`
  width: ${theme.sizes.lvl10};
  height: ${theme.sizes.lvl10};
  margin: 0px 20px;
  `}

  &:hover .innerIcon {
    transition: all 0.3s;
    width: ${({ theme }) => theme.sizes.sizeByFactor(3.5)};
    height: ${({ theme }) => theme.sizes.sizeByFactor(3.5)};
  }
`;

const SocialItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  max-width: max-content;
  padding: 0px ${({ theme }) => theme.sizes.lvl2};

  ${({ theme }) => theme.breakpoint('desktop')`
  padding:0px;
  margin: 0px ${theme.sizes.lvl3};
  `}
`;

const ThankYouStepsSection = ({ giftDownloadLink }) => {
  const { allAirtable, featuredGift } = useStaticQuery(graphql`
    query {
      featuredGift: file(relativePath: { eq: "featuredgift.png" }) {
        childImageSharp {
          gatsbyImageData(width: 300)
        }
      }
      allAirtable(
        filter: { table: { eq: "Session" }, data: { Gift: { eq: true } } }
        sort: { fields: data___GiftOrder }
      ) {
        edges {
          node {
            data {
              Name
              Slug
              Tagline {
                childMarkdownRemark {
                  html
                }
              }
              Content {
                childMarkdownRemark {
                  html
                }
              }
              Speaker {
                id
                data {
                  WebsiteUri
                  Avatar {
                    localFiles {
                      childImageSharp {
                        gatsbyImageData(width: 300)
                      }
                    }
                  }
                }
              }
              Image {
                localFiles {
                  childImageSharp {
                    gatsbyImageData(width: 300)
                  }
                }
              }
            }
            id
          }
        }
      }
    }
  `);

  const gifts = allAirtable?.edges?.map((edge) => edge?.node) || [];
  const {
    hasConferenceStarted,
    hasConferenceEnded,
    uri,
  } = useSiteMetaContext();

  return (
    <Background>
      <Wrapper id="thankyouSteps">
        <div id="stepBookmark">
          <StepCard>
            <CardTitle className="h3">
              Here's your link -
              <br />
              bookmark this page:
            </CardTitle>
            {hasConferenceEnded || !hasConferenceStarted ? (
              <CardLink to="/agenda" title="Link to agenda" rel="noopener">
                {uri}/agenda
              </CardLink>
            ) : (
              <>
                <CardLink to="/day" title="Link to day" rel="noopener">
                  {uri}/day
                </CardLink>
                <PaidContent
                  notPaid={
                    <CardContent>
                      This is your ACCESS LINK for each day's talks. Please make
                      a careful note of this link. Each day's content will be
                      available for 24 hours. Check your daily email reminders
                      for more information. You may need to clear your browser
                      history when a new day starts!
                    </CardContent>
                  }
                >
                  <></>
                </PaidContent>
              </>
            )}

            <CardIndexWrapper>
              <Index>1</Index>
            </CardIndexWrapper>
          </StepCard>
          <StepCard id="stepCalendar">
            <CardTitle className="h3">Connect with us for updates:</CardTitle>
            <SocialWrapper>
              <SocialItem>
                <SocialCircle
                  onClick={() =>
                    window.open(socialLinks.facebook, '_blank', 'noreferrer')
                  }
                >
                  <Icon
                    svg={facebook}
                    fillColor="#3b5998"
                    className="innerIcon"
                  />
                </SocialCircle>
              </SocialItem>

              {/* <SocialItem>
                <SocialCircle
                  onClick={() =>
                    window.open(socialLinks.twitter, '_blank', 'noreferrer')
                  }
                >
                  <Icon
                    svg={twitter}
                    fillColor="#1DA1F2"
                    size="MD"
                    className="innerIcon"
                  />
                </SocialCircle>
              </SocialItem> */}

              <SocialItem>
                <SocialCircle
                  onClick={() =>
                    window.open(socialLinks.instagram, '_blank', 'noreferrer')
                  }
                >
                  <Icon
                    svg={instagram}
                    fillColor="#E1306C"
                    size="MD"
                    className="innerIcon"
                  />
                </SocialCircle>
              </SocialItem>
            </SocialWrapper>

            {!hasConferenceEnded && (
              <SocialWrapper>
                <SocialItem>
                  <CalendarButton />
                </SocialItem>
              </SocialWrapper>
            )}

            <CardIndexWrapper>
              <Index>2</Index>
            </CardIndexWrapper>
          </StepCard>

          <div id="stepGifts">
            {giftDownloadLink && (
              <GiftCard
                title="Fatigue: 12 Secrets to Recovery eBook"
                image={featuredGift}
                extraTitle="Enjoy your FREE gifts!"
                buttonLink={giftDownloadLink}
                buttonLabel="Download Ebook"
              >
                If you or someone you love suffers from chronic fatigue in any
                capacity, The Secrets to Recovery ebook provides the best tips,
                proven techniques, and professional insights from Alex Howard
                and Anna Duschinsky, based on their 20+ years of firsthand
                experience helping people recover from ailments deemed
                impossible to recover from at{' '}
                <strong className="strong">
                  The Optimum Health Clinic in London
                </strong>
                .
                <CardIndexWrapper>
                  <Index>3</Index>
                </CardIndexWrapper>
              </GiftCard>
            )}
            {gifts
              .filter(({ data }) => data.Name && data.Slug)
              .map((gift, index) => (
                <GiftCard
                  key={`gift_${gift.id}`}
                  title={gift.data.Name}
                  image={
                    gift.data?.Image?.localFiles[0] ||
                    gift.data?.Speaker[0]?.data.Avatar.localFiles[0]
                  }
                  extraTitle={
                    index === 0 && !giftDownloadLink
                      ? 'Enjoy your FREE gifts!'
                      : null
                  }
                  buttonLink={`/session/${gift.data.Slug}`}
                >
                  {gift?.data?.Tagline?.childMarkdownRemark?.html && (
                    <div
                      className="p"
                      dangerouslySetInnerHTML={{
                        __html: gift.data.Tagline.childMarkdownRemark.html,
                      }}
                    />
                  )}
                  <p style={{ marginBottom: '0.5rem' }}>You will learn:</p>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: gift.data?.Content?.childMarkdownRemark.html,
                    }}
                  />
                  {gift.data?.Speaker[0]?.data?.WebsiteUri && (
                    <a
                      href={gift.data?.Speaker[0]?.data?.WebsiteUri}
                      alt="Visit Speakers Website"
                    >
                      Visit speakers website
                    </a>
                  )}
                  {index === 0 && !giftDownloadLink && (
                    <CardIndexWrapper>
                      <Index>3</Index>
                    </CardIndexWrapper>
                  )}
                </GiftCard>
              ))}
          </div>
        </div>
      </Wrapper>
    </Background>
  );
};

export default ThankYouStepsSection;
